import React, { useEffect, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import CommentThread from '../../../shared/components/comments/CommentThread';
import { ConfirmActionDialog } from '../../../shared/components/patterns';
import {
  createClientNote,
  deleteClientNote,
  getClientNotes,
} from '@/modules/clients/services/clients.service';

export default function ClientNotesPanel({
  clientId,
  open,
  loadNotes = getClientNotes,
  saveNote = createClientNote,
  removeNote = deleteClientNote,
  staff = [],
}) {
  const { currentUser } = useSession();
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState('');
  const [noteDeleteRequest, setNoteDeleteRequest] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!open || !clientId) {
        setNotes([]);
        setDeletingNoteId('');
        setError('');
        return;
      }

      setIsLoading(true);
      setError('');
      const result = await loadNotes(clientId);
      if (cancelled) return;

      setIsLoading(false);
      if (result?.ok === false) {
        setNotes([]);
        setError(result.message || 'Commenti cliente non disponibili.');
        return;
      }

      setNotes(result?.data?.notes || []);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [clientId, loadNotes, open]);

  async function submitNote(payload) {
    const body = String(payload?.body || '').trim();
    if (!body || isSaving || !clientId) return { ok: false };

    setIsSaving(true);
    setError('');
    const result = await saveNote(clientId, body);
    setIsSaving(false);

    if (result?.ok === false) {
      setError(result.message || 'Commento non salvato.');
      return result;
    }

    setNotes((current) => [...current, result.data.note]);
    return result;
  }

  async function submitReply(parentId, payload) {
    const body = String(payload?.body || '').trim();
    if (!body || !parentId || isSaving || !clientId) return { ok: false };

    setIsSaving(true);
    setError('');
    const result = await saveNote(clientId, body, parentId);
    setIsSaving(false);

    if (result?.ok === false) {
      setError(result.message || 'Risposta non salvata.');
      return result;
    }

    setNotes((current) => [...current, result.data.note]);
    return result;
  }

  async function deleteNote(noteId) {
    if (!noteId || deletingNoteId) return;

    setDeletingNoteId(noteId);
    setError('');
    const result = await removeNote(clientId, noteId);
    setDeletingNoteId('');

    if (!result?.ok) {
      setError(result.message || 'Commento non eliminato.');
      return;
    }

    setNotes((current) => current.filter((note) => String(note.id) !== String(noteId)));
  }

  return (
    <>
      <CommentThread
        key={clientId}
        title="Commenti"
        comments={notes}
        currentUser={currentUser}
        staff={staff}
        isLoading={isLoading}
        isSubmitting={isSaving}
        error={error}
        emptyLabel="Nessun commento presente per questo cliente."
        placeholder="Aggiungi un commento al cliente..."
        submitLabel="Aggiungi commento"
        onSubmitComment={submitNote}
        onSubmitReply={submitReply}
        onDeleteComment={(note) => setNoteDeleteRequest(note)}
        allowReplies
        disabled={!clientId || Boolean(deletingNoteId)}
      />
      <ConfirmActionDialog
        open={Boolean(noteDeleteRequest)}
        title="Eliminare questo commento cliente?"
        description="Il commento verra rimosso definitivamente."
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setNoteDeleteRequest(null)}
        onConfirm={async () => {
          const noteId = noteDeleteRequest?.id;
          setNoteDeleteRequest(null);
          await deleteNote(noteId);
        }}
      />
    </>
  );
}
